import type {
  ActionTypePrefix,
  References,
} from '@polyai/stream/utils/rich-text-input';

const REF_PREFIX_MAP = {
  sms: 'twilio_sms:',
  handoff: 'ho:',
  attributes: 'attr:',
  transitionFunctions: 'ft:',
  globalFunctions: 'fn:',
  entities: 'entity:',
  variables: 'vrbl:',
  translations: 'tn:',
} satisfies Record<References, ActionTypePrefix>;

// Build one combined regex for all keys
const combinedPattern = new RegExp(
  `\\{\\{(${Object.values(REF_PREFIX_MAP).join('|')})([^}]+)\\}\\}`,
  'g',
);

export const getPromptRefs = <
  T extends {
    [K in keyof typeof REF_PREFIX_MAP]?: Record<string, boolean>;
  },
>(
  prompt: string,
  initialState: T,
  resetReferences: boolean = true,
): T => {
  // Clone shallowly so we don't mutate the original
  const references: T = {} as T;
  Object.keys(initialState).forEach(k => {
    references[k as keyof typeof REF_PREFIX_MAP] = resetReferences
      ? {}
      : initialState[k as keyof typeof REF_PREFIX_MAP];
  });

  let match;
  while ((match = combinedPattern.exec(prompt)) !== null) {
    const [, prefix, id] = match;

    // Find the corresponding key for this prefix
    const key = (
      Object.keys(REF_PREFIX_MAP) as (keyof typeof REF_PREFIX_MAP)[]
    ).find(k => REF_PREFIX_MAP[k] === prefix);

    if (!key || !(key in references)) {
      continue;
    }

    const group = references[key] ?? {};

    if (!(id in group)) {
      group[id] = true;
    }
    if (resetReferences) {
      references[key] = group;
    } else {
      references[key] = {
        ...references[key],
        ...group,
      };
    }
  }

  return references;
};

export const replacePromptRefs = <
  T extends {
    [key in keyof typeof REF_PREFIX_MAP]?: Record<string, string>;
  },
>(
  prompt: string,
  replacementMap: T,
): string => {
  return prompt.replace(combinedPattern, match => {
    const [prefix, id] = match
      .replaceAll('{', '')
      .replaceAll('}', '')
      .split(':');

    const key = Object.keys(REF_PREFIX_MAP).find(
      k => REF_PREFIX_MAP[k as keyof typeof REF_PREFIX_MAP] === `${prefix}:`,
    );
    const replacements = replacementMap[
      key as keyof typeof replacementMap
    ] as Record<string, string>;
    const value = replacements?.[id];

    if (value) {
      return value;
    }

    return match;
  });
};
